import React, { useState, useEffect } from 'react';
import { Button, Drawer, Form, Input, Select, Radio, message, Checkbox, Spin, Col, Row } from "antd";
import { ArrowLeftOutlined, CheckOutlined } from "@ant-design/icons";
import './metaDataGroup.css';
import { metaDataGroupManage, checkFieldExistsInWorkflowMetadata } from '../../services/metadataGroupService';
import Grid from "../../components/commonFields/Grid";

const { Option } = Select;
const GROUPTYPE_DEFAULT = 0;
const GROUPTYPE_GLOBAL = 1;
const defaultIndexSelectValues = ["key_1", "key_2", "key_3"];

const MetadataFields = ({ group, goBack, openDrawer = false, allGroups = [], onSaveGroup }) => {
    const { groupName, configData } = group;
    const [fieldsData, setFieldsData] = useState(configData?.Fields || []);
    const [isDrawerVisible, setIsDrawerVisible] = useState(openDrawer);
    const [editRecordKey, setEditRecordKey] = useState(null);
    const [groupNameInput, setGroupNameInput] = useState(group?.groupName || "");
    const [groupType, setGroupType] = useState(group?.groupType === GROUPTYPE_GLOBAL ? GROUPTYPE_GLOBAL : GROUPTYPE_DEFAULT);
    const [description, setDescription] = useState(group?.description || "");
    const [loading, setLoading] = useState(false);

    //Add Index and Unique Node in field object from Group json Node
    // const updateFieldsObject = (inputJson) => {
    //     debugger;
    //     const indexesReverseMap = Object.entries(inputJson.Indexes).reduce((acc, [key, value]) => {
    //         acc[value] = key;
    //         return acc;
    //     }, {});

    //     // Update Fields with Unique and Index if matched
    //     const updatedFields = inputJson.Fields.map(field => {
    //         if (inputJson.Unique.includes(field.Name)) {
    //             field.Unique = true;
    //         }

    //         if (indexesReverseMap[field.Name]) {
    //             field.Index = indexesReverseMap[field.Name];
    //         }
    //         return field;
    //     });
    //     setFieldsData(updatedFields);
    // } 

    const [form] = Form.useForm();
    useEffect(() => {
        if (openDrawer) {
            setIsDrawerVisible(true);
            form.resetFields();
        }
    }, [openDrawer, form]);

    const handleEditClick = (field) => {
        // const isUnique = group?.configData?.Unique?.includes(field.Name);
        // const fieldIndex = Object.entries(group?.configData?.Indexes || {}).find(([key, value]) => value === field.Name)?.[0] || "";
        const isUnique = field.Unique;
        const fieldIndex = field.Index;
        setEditRecordKey(field.Name);
        form.setFieldsValue({
            ...field,
            Index: fieldIndex || "",
            Unique: isUnique || false,
            HashRequired: field?.HashRequired === "Yes" ? "Yes" : "No",
            IsSecured: field?.IsSecured === "Yes" ? "Yes" : "No",
            HashPositions: field?.HashPositions === "Yes" ? "Yes" : "No",
        });

        setIsDrawerVisible(true);
    };

    const handleAddClick = async () => {
        try {
            if (!group?.groupId) {
                await form.validateFields(["GroupName"]);
            }

            setEditRecordKey(null);
            form.resetFields(["Name", "Value", "DataType", "Index", "Unique", "HashRequired", "HashPositions", "IsSecured"]);

            setIsDrawerVisible(true);
        } catch (error) {
            console.warn("Group Name validation failed:", error);
        }
    };

    const closeDrawer = () => {
        setIsDrawerVisible(false);
    };

    const handleDelete = (field) => {
        if (!field?.Name) {
            message.error("Invalid field selected for deletion.");
            return;
        }

        const filteredFields = fieldsData.filter(f => f.Name.toLowerCase() !== field.Name.toLowerCase());
        setFieldsData(filteredFields);
        message.success("Field deleted successfully!");
    };

    const columns = [
        {
            title: "Field Name",
            dataIndex: "Name",
            key: "Name",
        },
        {
            title: "Field Index",
            dataIndex: "Index",
            key: "Index",
            width: 100,
            tag: true,
            tagColor: "#f9a11b",
        },
        {
            title: "Unique",
            dataIndex: "Unique",
            key: "Unique",
            width: 100,
            tag: true,
            tagColor: "#0e8617",
            boolean: true, //consider the 1 or 0 is true or false to set 'tag' if it is 'true'
        },
        {
            title: 'Actions',
            key: 'actions',
            dataIndex: 'actions',
            width: 100,
            fixed: 'right',
            render: ['edit', 'delete']
        },
    ];

    const handleDrawerAddField = async () => {
        try {
            const values = await form.validateFields();

            if (!values.Name || values.Name.trim() === "") {
                message.error("Field Name should not be empty");
                return;
            }

            const groupId = group?.groupId;
            
            // Only check field existence if groupId exists (not a new group)
            // Note: This endpoint may not be available, so we'll silently skip if it fails
            if (groupId) {
                try {
                    const resObj = await checkFieldExistsInWorkflowMetadata(groupId, values.Name.trim());
                    
                    if (resObj?.data?.success) {
                        const isExist = resObj?.data?.data?.fieldsExists;
                        if (isExist?.result) {
                            const resultArray = JSON.parse(isExist.result);
                            if (resultArray?.length > 0) {
                                message.error("The field name is already in use in a group that's part of an active workflow.");
                                return;
                            }
                        }
                    }
                    // Silently continue if check fails - don't show warning
                } catch (error) {
                    // Silently skip field existence check if endpoint doesn't exist or fails
                    // This is expected for new groups or if the endpoint isn't configured
                    console.log("Field existence check skipped (endpoint may not be available)");
                }
            }

            // Add or update the field
            const fieldToAdd = {
                ...values,
                Name: values.Name.trim()
            };
            let updatedFields;
            if (editRecordKey) {
                updatedFields = fieldsData.map(field =>
                    field.Name === editRecordKey ? { ...field, ...fieldToAdd } : field
                );
            } else {
                updatedFields = [...fieldsData, fieldToAdd];
            }
            setFieldsData(updatedFields);
            setIsDrawerVisible(false);
            message.success(editRecordKey ? "Field updated!" : "Field added!");

        } catch (error) {
            console.error("Error adding/updating field:", error);
            if (error.errorFields) {
                message.error("Please fill all required field data.");
            } else {
                message.error("An error occurred while adding/updating the field.");
            }
        }
    };

    const handleSaveGroup = async () => {
        try {
            if (!group?.groupId) {
                await form.validateFields(["GroupName"]);
            }
            setLoading(true);
            // Get the value from form field to ensure it's synced
            const formGroupName = form.getFieldValue("GroupName") || groupNameInput;
            const trimmedGroupNameInput = (formGroupName || groupNameInput || "").trim();
            
            if (!trimmedGroupNameInput) {
                message.error("Group Name is required.");
                setLoading(false);
                return;
            }
            
            const trimmedDescription = description.trim();
            const Indexes = {};
            const uniqueFields = [];

            fieldsData.forEach((field) => {
                // if (field.Index) {
                //     Indexes[field.Index] = field.Name;
                // }
                // if (field.Unique === true) {
                //     uniqueFields.push(field.Name);
                // }
                if (field.Index) {
                    for (const key in Indexes) {
                        if (Indexes[key] === field.Name) {
                            delete Indexes[key];
                        }
                    }
                    Indexes[field.Index] = field.Name;
                }
                if (field.Unique === true) {
                    const index = uniqueFields.indexOf(field.Name);
                    if (index !== -1) {
                        uniqueFields.splice(index, 1);
                    }
                    uniqueFields.push(field.Name);
                }
            });

            const transformedFields = fieldsData.map((field) => ({
                Name: field.Name,
                Value: field.Value || "",
                DataType: field.DataType,
                HashRequired: field.HashRequired || "No",
                HashPositions: field.HashPositions || "",
                IsSecured: field.IsSecured || "No",
                Index: field.Index || "",
                Unique: field.Unique || false,
            }));

            const updatedConfigData = {
                GroupName: trimmedGroupNameInput,
                Unique: uniqueFields,
                Indexes: Indexes,
                Fields: transformedFields
            };

            const payload = {
                groupId: group?.groupId || 0,
                groupName: trimmedGroupNameInput,
                groupType: groupType,
                actionType: group?.groupId ? "update" : "add",
                fieldConfigData: JSON.stringify(updatedConfigData),
                groupDesc: trimmedDescription,
            };

            const response = await metaDataGroupManage(payload);

            console.log('📥 Frontend - Full response:', response);
            console.log('📥 Frontend - response.success:', response?.success);
            console.log('📥 Frontend - response.data:', response?.data);
            console.log('📥 Frontend - response.data.group:', response?.data?.group);

            if (!response) {
                message.error("Failed to save group. No response from server.");
                return;
            }

            if (response?.success) {
                const groupResult = response?.data?.group?.[0];
                console.log('📥 Frontend - groupResult:', groupResult);
                
                if (groupResult) {
                    const isExistsGroupName = groupResult.isExists;
                    const returnedGroupId = groupResult.groupId;
                    
                    console.log('📥 Frontend - isExists:', isExistsGroupName, 'type:', typeof isExistsGroupName);
                    console.log('📥 Frontend - groupId:', returnedGroupId, 'type:', typeof returnedGroupId);
                    
                    // Check if group name already exists FIRST
                    // isExists can be 'true', 'false' (string) or true, false (boolean)
                    const nameExists = isExistsGroupName === 'true' || isExistsGroupName === true || String(isExistsGroupName).toLowerCase() === 'true';
                    
                    if (nameExists) {
                        console.log('📥 Frontend - Group name exists, showing error');
                        message.error('Group Name already Exists');
                        setLoading(false);
                        return;
                    }
                    
                    // If name doesn't exist, check if we got a valid groupId
                    // For new groups, groupId should be a UUID (not "0" or 0)
                    // For updates, groupId should match the existing groupId
                    const isValidGroupId = returnedGroupId && 
                                         returnedGroupId !== '0' && 
                                         returnedGroupId !== 0 && 
                                         String(returnedGroupId).trim() !== '';
                    
                    if (isValidGroupId) {
                        console.log('📥 Frontend - Valid groupId, saving success');
                        // Success - new group created or existing group updated
                        // Include the updated configData so parent has the latest fields
                        const updatedGroup = {
                            ...group,
                            groupId: returnedGroupId,
                            groupName: trimmedGroupNameInput,
                            description: trimmedDescription,
                            configData: updatedConfigData // Include the saved configData with fields
                        };
                        onSaveGroup(updatedGroup);
                        message.success(`Group ${group?.groupId ? 'updated' : 'saved'} successfully!`);
                        setLoading(false);
                        return;
                    } else {
                        console.log('📥 Frontend - Invalid groupId:', returnedGroupId);
                        // Invalid response - groupId is 0 or missing
                        message.error('Failed to save group. Invalid group ID returned from server.');
                        setLoading(false);
                        return;
                    }
                } else {
                    console.log('📥 Frontend - No groupResult in response.data.group');
                    console.log('📥 Frontend - response.data:', response?.data);
                    message.error(response?.businessRules?.[0] || response?.message || "Failed to save group. Invalid response structure.");
                    setLoading(false);
                    return;
                }
            } else {
                console.log('📥 Frontend - response.success is false');
                console.log('📥 Frontend - businessRules:', response?.businessRules);
                const errorMsg = Array.isArray(response?.businessRules) 
                    ? response.businessRules[0] 
                    : response?.businessRules || response?.message || "Failed to save group.";
                message.error(errorMsg);
                setLoading(false);
                return;
            }

        } catch (error) {
            console.error("❌ Frontend - Error saving group:", error);
            console.error("❌ Frontend - Error response:", error?.response);
            console.error("❌ Frontend - Error response data:", error?.response?.data);
            
            // Check if it's a network error or API endpoint not found
            if (error?.response?.status === 404) {
                message.error("API endpoint not found. Please check if the metadata group service is configured correctly.");
            } else if (error?.response?.status >= 500) {
                const errorMsg = error?.response?.data?.businessRules?.[0] || 
                               error?.response?.data?.message || 
                               "Server error occurred while saving the group. Please try again later.";
                message.error(errorMsg);
            } else if (error?.response?.data?.businessRules?.[0]) {
                message.error(error.response.data.businessRules[0]);
            } else if (error?.response?.data?.message) {
                message.error(error.response.data.message);
            } else if (error?.message) {
                message.error(`Error: ${error.message}`);
            } else {
                message.error("An error occurred while saving the group.");
            }
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (group?.groupName) {
            form.setFieldsValue({
                GroupName: group.groupName,
                Description: group.description
            });
        } else {
            console.log("GroupName is undefined or empty");
        }
    }, [group, form]);

    // Update fieldsData when group.configData changes (e.g., when coming back after saving)
    useEffect(() => {
        // Only update if we have a valid group with configData
        if (group?.configData?.Fields && Array.isArray(group.configData.Fields)) {
            console.log('🔄 Updating fieldsData from group.configData:', group.configData.Fields);
            setFieldsData(group.configData.Fields);
        } else if (group && (!group.configData || !group.configData.Fields)) {
            // If group exists but has no configData or Fields, reset to empty array
            setFieldsData([]);
        }
    }, [group?.groupId, group?.configData]);

    return (
        <>
            <div>
                <h2>{groupName}</h2>
                <Form
                    form={form}
                    layout="vertical"
                    style={{ width: "100%" }}
                    initialValues={{
                        GroupType: groupType === GROUPTYPE_GLOBAL, // Must be boolean for checkbox
                    }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={12} lg={5}>
                            <Form.Item
                                label="Group Name"
                                name="GroupName"
                                rules={[
                                    { required: true, message: "Field is required" },
                                    () => ({
                                        validator(_, value) {
                                            const trimmedValue = value?.trim().toLowerCase();
                                            const isDuplicate = allGroups.some(
                                                (g) =>
                                                    g.groupName.trim().toLowerCase() === trimmedValue &&
                                                    g.groupId !== group?.groupId
                                            );
                                            if (isDuplicate) {
                                                return Promise.reject(new Error("Group name already exists."));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input
                                    placeholder="Group Name"
                                    value={groupNameInput}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setGroupNameInput(newValue);
                                        // Sync with form field
                                        form.setFieldsValue({ GroupName: newValue });
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={5}>
                            <Form.Item label="Description" name="Description">
                                <Input placeholder="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={7}>
                            <Form.Item label="Global Group" name="GroupType" valuePropName="checked">
                                <Checkbox onChange={(e) => setGroupType(e.target.checked ? GROUPTYPE_GLOBAL : GROUPTYPE_DEFAULT)} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={2}>
                            <Form.Item label=" ">
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={goBack}
                                    style={{ width: '100%' }}
                                >
                                    Back
                                </Button>
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Form.Item label=" ">
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    onClick={handleSaveGroup}
                                    style={{ width: '100%', color: 'white' }}
                                    disabled={!groupNameInput || !groupNameInput.trim()}
                                >
                                    {group?.groupId ? "Update Group" : "Save Group"}
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Form>

                <Spin spinning={loading}>
                    <Grid
                        columnData={{ columns: columns }}
                        tableData={fieldsData}
                        // loading={loading}
                        rowKey="Name"
                        searchButton={true}
                        downloadCSV={true}
                        showEdit={true}
                        showDelete={true}
                        csvFileName="ClientDataGroup"
                        paginationConfig={{ pageSizeOptions: ['10', '20', '50', '100'] }}
                        className="custom-grid"
                        handleEdit={handleEditClick}
                        handleDelete={handleDelete}
                        createButton={true}
                        createButtonName={'Add Fields'}
                        createButtonIcon={<CheckOutlined />}
                        handleCreate={handleAddClick}
                    />
                </Spin>
                <Drawer
                    title={editRecordKey ? "Edit Field" : "Add Field"}
                    // style={{ width: '25%' }}
                    open={isDrawerVisible}
                    onClose={closeDrawer}
                    className="custom-drawers cus-drawer-sm"
                    footer={
                        <div style={{ textAlign: 'right' }}>
                            <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
                                Cancel
                            </Button>
                            <Button onClick={handleDrawerAddField} type="primary">
                                {editRecordKey ? 'Update Field' : 'Add Field'}
                            </Button>
                        </div>
                    }
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            label="Field Name"
                            name="Name"
                            // rules={[
                            //     { required: true, message: "Field is required" },
                            //     ({ getFieldValue }) => ({
                            //         validator(_, value) {
                            //             const trimmedName = value?.trim();
                            //             const isDuplicate = fieldsData.some(
                            //                 field => field.Name.toLowerCase() === trimmedName?.toLowerCase() &&
                            //                     field.Name !== editRecordKey // Allow the same name during edit
                            //             );
                            //             if (isDuplicate) {
                            //                 return Promise.reject(new Error("Field Name already exists"));
                            //             }
                            //             return Promise.resolve();
                            //         }
                            //     })
                            // ]}
                            rules={[
                                { required: true, message: "Field is required" },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const trimmedName = value?.trim();

                                        if (/\s/.test(trimmedName)) {
                                            return Promise.reject(new Error("Spaces are not allowed"));
                                        }

                                        if (/[^a-zA-Z0-9_]/.test(trimmedName)) {
                                            return Promise.reject(new Error("Only letters, numbers, and underscores are allowed"));
                                        }

                                        const isDuplicate = fieldsData.some(
                                            field => field.Name.toLowerCase() === trimmedName?.toLowerCase() &&
                                                field.Name !== editRecordKey // Allow same name during edit
                                        );
                                        if (isDuplicate) {
                                            return Promise.reject(new Error("Field Name already exists"));
                                        }

                                        return Promise.resolve();
                                    }
                                })
                            ]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item label="Data Type" name="DataType" rules={[{ required: true, message: "Field is required" }]}>
                            <Select>
                                <Option value="Text">Text</Option>
                                <Option value="Number">Number</Option>
                                <Option value="Boolean">Boolean</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Index"
                            name="Index"
                            rules={[
                                { required: false, message: "Field is required" },
                                // ({ getFieldValue }) => ({
                                //     validator(_, value) {
                                //         if (!value) return Promise.resolve();
                                //         const isDuplicate = fieldsData.some(field => {
                                //             const existingIndex = Object.entries(group?.configData?.Indexes || {}).find(
                                //                 ([key, val]) => key === value && field.Name !== editRecordKey && val === field.Name
                                //             );
                                //             return !!existingIndex;
                                //         });

                                //         if (isDuplicate) {
                                //             return Promise.reject(new Error(`Index is already used in another field.`));
                                //         }

                                //         return Promise.resolve();
                                //     }
                                // })
                            ]}
                        >
                            {/* <Select>
                                <Option value="">Select</Option>
                                <Option value="key_1">Key 1</Option>
                                <Option value="key_2">Key 2</Option>
                                <Option value="key_3">Key 3</Option>
                            </Select> */}
                            <Select>
                                <Option value="">Select</Option>
                                {defaultIndexSelectValues.map((key) => {
                                    const isUsedByAnotherField = fieldsData.some(
                                        (field) => field.Index === key && field.Name !== editRecordKey
                                    );
                                    return (
                                        <Option key={key} value={key} disabled={isUsedByAnotherField}>
                                            {key.replace("_", " ").toUpperCase()}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Default Value" name="Value">
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Unique"
                            name="Unique"
                            valuePropName="checked"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        // Only validate if "Unique" is checked
                                        if (value) {
                                            const isDuplicateUnique = fieldsData.some(
                                                field =>
                                                    field.Unique === true &&
                                                    field.Name !== editRecordKey // Allow during edit
                                            );

                                            if (isDuplicateUnique) {
                                                return Promise.reject(
                                                    new Error("A field with Unique is already associated")
                                                );
                                            }
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Checkbox>Is Unique</Checkbox>
                        </Form.Item>


                        <Form.Item label="Hash Required" name="HashRequired">
                            <Radio.Group defaultValue="No">
                                <Radio value="Yes">Yes</Radio>
                                <Radio value="No">No</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Hash Positions" name="HashPositions">
                            <Radio.Group defaultValue="No">
                                <Radio value="Yes">Yes</Radio>
                                <Radio value="No">No</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Is Secured" name="IsSecured">
                            <Radio.Group defaultValue="No">
                                <Radio value="Yes">Yes</Radio>
                                <Radio value="No">No</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Form>
                </Drawer>
            </div>

        </>
    );
};

export default MetadataFields;